'use client';

/**
 * ObeyaPlanLensKpiStripCard — the headline figures as ONE compact line.
 * Which figures is the placement prop `kpis` (keys of the served `kpis`);
 * every value and its order arrive from the BFF, this only formats.
 *
 * @see klyde_edge/klyde_intelligence/projects/plan_kpis.py
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensKpiStripCard
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { money, usePlanLenses, type Kpi } from './use-plan-lenses';

const FORMAT: Record<Kpi['format'], (v: number) => string> = {
  money,
  integer: (v) => v.toLocaleString(),
  percent: (v) => `${v}%`,
};

const show = (v: number | null, format: Kpi['format']) => (v == null ? '—' : FORMAT[format](v));

interface Props { kpis: string[] }

export function ObeyaPlanLensKpiStripCard({ kpis }: Props) {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_kpi_strip',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: 'Plan KPIs',
    json: d ? d.kpis : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-4 text-center')}>…</div>;
  if (!d) return null;

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm px-5 py-4 flex flex-wrap gap-x-10 gap-y-3">
      {kpis.map((key) => {
        const k = d.kpis[key];
        if (!k) return null;
        const chip = k.chip
          ? (k.chip.text ?? [show(k.chip.value, k.chip.format), k.chip.label ? d.labels[k.chip.label] : ''].filter(Boolean).join(' '))
          : '';
        return (
          <div key={key} className="flex flex-col gap-0.5">
            <span className={cn(text.eyebrow)}>{d.labels[k.label]}</span>
            <span className="flex items-baseline gap-2">
              <span className={cn(text.value)}>{show(k.value, k.format)}</span>
              {chip && <span className={cn(text.muted)}>{chip}</span>}
            </span>
          </div>
        );
      })}
    </div>
  );
}
